import * as React from "react"
import { useTranslation } from "react-i18next"
import { Icon, cn } from "@nexus/ui"

const WORKFLOW_STEPS = [
  { titleKey: "opportunities.infoBanner.step1Title", labelKey: "opportunities.infoBanner.step1Label" },
  { titleKey: "opportunities.infoBanner.step2Title", labelKey: "opportunities.infoBanner.step2Label" },
  { titleKey: "opportunities.infoBanner.step3Title", labelKey: "opportunities.infoBanner.step3Label" },
] as const

export interface OpportunitiesInfoBannerProps {
  /** Whether the banner is revealed. Collapsed (hidden) by default. */
  open: boolean
  /** Called when the user collapses the banner via its chevron. */
  onClose: () => void
  className?: string
}

/**
 * Inline, full-width onboarding banner for the Opportunities workflow. Replaces
 * the prior floating popover so the guidance reads as a banner, not an overlay.
 * Collapsed by default; the page's lightbulb toggle reveals it.
 */
export function OpportunitiesInfoBanner({ open, onClose, className }: OpportunitiesInfoBannerProps) {
  const { t } = useTranslation()

  if (!open) return null

  return (
    <section
      aria-label={t("opportunities.infoBanner.headline")}
      className={cn(
        "shrink-0 overflow-hidden rounded-lg border border-border/60 bg-card",
        // Entrance: confident ease-out-quint slide, disabled under reduced motion.
        "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 motion-safe:duration-300",
        "motion-safe:[animation-timing-function:cubic-bezier(0.22,1,0.36,1)]",
        className,
      )}
    >
      <div className="flex items-start gap-4 p-4">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold leading-snug text-foreground">
              {t("opportunities.infoBanner.headline")}
            </p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t("opportunities.infoBanner.description")}
            </p>
          </div>

          <ol className="flex flex-col gap-3 border-t border-border/60 pt-3 sm:flex-row sm:items-center sm:gap-2">
            {WORKFLOW_STEPS.map((step, i) => (
              <React.Fragment key={step.titleKey}>
                {i > 0 && (
                  <Icon
                    name="chevron-right"
                    size="xs"
                    aria-hidden
                    className="hidden shrink-0 text-muted-foreground/60 motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:duration-300 sm:block"
                    style={{ animationDelay: `${140 + i * 80}ms` }}
                  />
                )}
                <li
                  className="flex min-w-0 flex-1 items-baseline gap-2 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:fill-mode-both motion-safe:duration-300 motion-safe:[animation-timing-function:cubic-bezier(0.22,1,0.36,1)]"
                  style={{ animationDelay: `${140 + i * 80}ms` }}
                >
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold tabular-nums text-primary">
                    {i + 1}
                  </span>
                  <div className="flex min-w-0 flex-col">
                    <span className="text-xs font-semibold leading-snug text-foreground">
                      {t(step.titleKey)}
                    </span>
                    <span className="mt-0.5 text-xs leading-snug text-muted-foreground">
                      {t(step.labelKey)}
                    </span>
                  </div>
                </li>
              </React.Fragment>
            ))}
          </ol>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label={t("opportunities.infoBanner.collapseAria")}
          className="-mr-1 -mt-1 flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-[color,background-color,transform] duration-150 hover:bg-muted hover:text-foreground active:scale-90 motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
        >
          <Icon name="chevron-up" size="xs" color="inherit" />
        </button>
      </div>
    </section>
  )
}
